import React, { useState, useEffect } from 'react';
import { Share2, CreditCard, DollarSign, ShoppingCart, Check, Trash2 } from 'lucide-react';
import { Modal } from '../common';
import { ClassData, UserInfo } from '../../types';
import { formatDate } from '../../helpers/dateUtils';
import { formatTime, getVenmoUrl, isValidEmail } from '../../helpers/formatUtils';
import { User } from 'firebase/auth';
import { useCart } from '../../context';

interface BookingModalProps {
    isOpen: boolean;
    onClose: () => void;
    bookingClass: ClassData | null;
    userInfo: UserInfo;
    setUserInfo: (info: UserInfo) => void;
    selectedDay: Date;
    onConfirm: (paymentOption: 'venmo' | 'arrangement' | null, userInfo?: UserInfo) => Promise<boolean>;
    onShare: (type: 'class', id: string, name: string) => void;
    venmoUsername: string;
    isSubmitting?: boolean;
    // Auth props
    user?: User | null;
    isGoogleUser?: boolean;
    onSignInWithGoogle?: () => Promise<void>;
    // Registered status props
    isUserSignedUp?: boolean;
    onCancelRegistration?: () => void;
    // Callback for add-to-cart (to close parent modals on mobile)
    onAddToCart?: () => void;
}

const BookingModal: React.FC<BookingModalProps> = ({ 
    isOpen, onClose, bookingClass, userInfo, setUserInfo, selectedDay, onConfirm, onShare, venmoUsername, isSubmitting = false,
    user, isGoogleUser = false, onSignInWithGoogle, isUserSignedUp = false, onCancelRegistration, onAddToCart
}) => {
    // Default to 'venmo' for paid classes, null for free classes
    const [paymentOption, setPaymentOption] = useState<'venmo' | 'arrangement' | null>(
        bookingClass?.paymentType === 'venmo' ? 'venmo' : null
    );
    const [localUserInfo, setLocalUserInfo] = useState<UserInfo>(userInfo);
    const { addToCart, removeFromCart, isInCart } = useCart();

    const classIsInCart = bookingClass ? isInCart(bookingClass.id) : false;

    useEffect(() => {
        setLocalUserInfo(userInfo);
    }, [userInfo]);

    // Reset payment option when booking class changes
    useEffect(() => {
        if (bookingClass?.paymentType === 'venmo') {
            setPaymentOption('venmo');
        } else {
            setPaymentOption(null);
        }
    }, [bookingClass]);

    // Auto-fill user info from Google sign-in
    useEffect(() => {
        if (isGoogleUser && user) {
            const names = (user.displayName || '').split(' ');
            const firstName = names[0] || '';
            const lastName = names.slice(1).join(' ') || '';
            const email = user.email || '';
            
            // Only auto-fill if fields are empty
            setLocalUserInfo(prev => ({
                firstName: prev.firstName || firstName,
                lastName: prev.lastName || lastName,
                email: prev.email || email
            }));
        }
    }, [isGoogleUser, user]);

    const handleGoogleSignIn = async () => {
        if (onSignInWithGoogle) {
            await onSignInWithGoogle();
        }
    };

    const handleAddToCart = () => {
        if (bookingClass) {
            addToCart(bookingClass);
            // Close this modal
            onClose();
            // Also close parent modals (e.g., DayView on mobile)
            if (onAddToCart) {
                onAddToCart();
            }
        }
    };

    const handleRemoveFromCart = () => {
        if (bookingClass) {
            removeFromCart(bookingClass.id);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={isUserSignedUp ? `Manage: ${bookingClass?.name}` : `Book: ${bookingClass?.name}`}>
            <div className="space-y-4">
                <div className="text-sm text-slate-500 mb-4 pb-4 border-b border-slate-700 relative">
                    {bookingClass && (
                    <>
                        <div className="flex justify-between items-start">
                            <div>
                                <p>{formatDate(selectedDay)} @ {formatTime(bookingClass.startTime)}</p>
                                {bookingClass.paymentType === 'venmo' && (
                                    <p className="font-bold text-emerald-500 mt-1">
                                        Cost: {bookingClass.cost ? '$'+bookingClass.cost : 'Pay What You Can'}
                                    </p>
                                )}
                            </div>
                            <button 
                                onClick={() => onShare('class', bookingClass.id, bookingClass.name)}
                                className="text-fuchsia-400 hover:text-fuchsia-300"
                            >
                                <Share2 size={20} />
                            </button>
                        </div>
                    </>
                    )}
                </div>

                {/* Already Registered View */}
                {isUserSignedUp ? (
                    <div className="space-y-4 text-center">
                        <div className="bg-emerald-900/20 border border-emerald-700 rounded-lg p-4">
                            <div className="flex items-center justify-center gap-2 text-emerald-400 mb-2">
                                <Check size={20} />
                                <span className="font-bold">You're Registered!</span>
                            </div>
                            <p className="text-slate-400 text-sm">
                                You're already signed up for this class.
                            </p>
                        </div>
                        
                        {onCancelRegistration && (
                            <button
                                onClick={onCancelRegistration}
                                className="w-full py-2 bg-rose-900/30 text-rose-400 rounded border border-rose-900/50 hover:bg-rose-900/50 font-bold flex items-center justify-center gap-2"
                            >
                                <Trash2 size={16} /> Cancel Registration
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Quick sign-in option for anonymous users */}
                {!isGoogleUser && onSignInWithGoogle && (
                    <button
                        type="button"
                        onClick={handleGoogleSignIn}
                        className="w-full py-2 px-4 border border-slate-600 rounded flex items-center justify-center gap-2 text-slate-300 hover:bg-slate-800 transition-colors text-sm"
                    >
                        <svg className="w-4 h-4" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Sign in with Google to auto-fill
                    </button>
                )}

                <input className="w-full p-2 border border-slate-600 rounded text-slate-200 placeholder-slate-500 bg-slate-800" placeholder="First Name" value={localUserInfo.firstName} onChange={e => setLocalUserInfo({...localUserInfo, firstName: e.target.value})} />
                <input className="w-full p-2 border border-slate-600 rounded text-slate-200 placeholder-slate-500 bg-slate-800" placeholder="Last Name" value={localUserInfo.lastName} onChange={e => setLocalUserInfo({...localUserInfo, lastName: e.target.value})} />
                <input className="w-full p-2 border border-slate-600 rounded text-slate-200 placeholder-slate-500 bg-slate-800" placeholder="Email" type="email" value={localUserInfo.email} onChange={e => setLocalUserInfo({...localUserInfo, email: e.target.value})} />
                
                {bookingClass?.paymentType === 'venmo' && (
                    <div className="bg-slate-800 p-4 rounded border border-slate-600 space-y-3">
                        <p className="font-bold text-slate-300 flex items-center gap-2"><CreditCard size={16}/> Payment Method</p>
                        
                        {/* Payment Method Radio Group */}
                        <div className="space-y-2">
                            <label className="flex items-center gap-3 p-3 rounded border border-slate-600 cursor-pointer hover:bg-slate-700/50 transition-colors">
                                <input
                                    type="radio"
                                    name="paymentMethod"
                                    value="venmo"
                                    checked={paymentOption === 'venmo'}
                                    onChange={() => setPaymentOption('venmo')}
                                    className="w-4 h-4 text-blue-500 border-slate-500 focus:ring-blue-500"
                                />
                                <div className="flex items-center gap-2">
                                    <DollarSign size={16} className="text-blue-400" />
                                    <span className="text-slate-200">Pay with Venmo</span>
                                </div>
                            </label>
                            
                            {bookingClass.allowArrangements && (
                                <label className="flex items-center gap-3 p-3 rounded border border-slate-600 cursor-pointer hover:bg-slate-700/50 transition-colors">
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value="arrangement"
                                        checked={paymentOption === 'arrangement'}
                                        onChange={() => setPaymentOption('arrangement')}
                                        className="w-4 h-4 text-emerald-500 border-slate-500 focus:ring-emerald-500"
                                    />
                                    <span className="text-slate-200">I have made alternate arrangements</span>
                                </label>
                            )}
                        </div>
                    </div>
                )}

                {/* Primary Action: Register Button */}
                <button 
                    onClick={async () => {
                        setUserInfo(localUserInfo);
                        const success = await onConfirm(paymentOption, localUserInfo);
                        if (success && bookingClass) {
                            // Remove from cart if it was there (prevents mixed state)
                            if (isInCart(bookingClass.id)) {
                                removeFromCart(bookingClass.id);
                            }
                            // Open Venmo AFTER successful registration
                            if (paymentOption === 'venmo') {
                                window.open(getVenmoUrl(Number(bookingClass.cost), bookingClass.name, venmoUsername), '_blank');
                            }
                        }
                    }} 
                    disabled={isSubmitting || !localUserInfo.email || !isValidEmail(localUserInfo.email) || (bookingClass?.paymentType === 'venmo' && !paymentOption)} 
                    className="w-full py-3 mt-2 bg-gradient-to-r from-rose-600 to-fuchsia-600 text-white font-bold rounded disabled:opacity-50 hover:from-rose-500 hover:to-fuchsia-500 shadow-lg">
                    {isSubmitting ? 'Registering...' : (
                        bookingClass?.paymentType === 'venmo' 
                            ? (paymentOption === 'venmo' ? 'Register & Pay on Venmo' : 'Register') 
                            : 'Register'
                    )}
                </button>

                {/* Secondary Action: Add to Cart (link-style) */}
                {bookingClass && (
                    <div className="text-center mt-3">
                        {classIsInCart ? (
                            <button
                                onClick={handleRemoveFromCart}
                                className="text-slate-400 hover:text-slate-200 text-sm flex items-center justify-center gap-1.5 mx-auto transition-colors"
                            >
                                <ShoppingCart size={14} />
                                Remove from Cart
                            </button>
                        ) : (
                            <button
                                onClick={handleAddToCart}
                                className="text-sm flex items-center justify-center gap-1.5 mx-auto transition-colors text-fuchsia-400 hover:text-fuchsia-300"
                            >
                                <ShoppingCart size={14} />
                                Add to Cart
                            </button>
                        )}
                    </div>
                )}
                    </>
                )}
            </div>
        </Modal>
    );
};

export default BookingModal;
